import { useState, useEffect } from "react";
import { Plus, Trash2, Loader2, Upload, User } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useRoles } from "@/hooks/queries/useRoles";
import { interviewerService } from "@/services/interviewerService";
import { toast } from "sonner";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import userIcon from "../../assets/icons/user.svg";

export default function InterviewerManagement() {
  const [showAddInterviewerModal, setShowAddInterviewerModal] = useState(false);
  const [newInterviewer, setNewInterviewer] = useState({
    name: "",
    email: "",
    password: "",
    role: "Interviewer",
    image: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch roles from backend when modal opens
  const { data: rolesData, isLoading: rolesLoading } = useRoles(1, {}, {
    enabled: showAddInterviewerModal // Only fetch when modal is open
  });

  // Filter to only show Interviewer role, exclude admin roles
  const availableRoles = (rolesData?.data || []).filter(role => 
    role.title && role.title.toLowerCase() === 'interviewer'
  );

  const [interviewers, setInterviewers] = useState([]);
  const [isLoadingInterviewers, setIsLoadingInterviewers] = useState(true);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [interviewerToDelete, setInterviewerToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch interviewers when component mounts
  useEffect(() => {
    const fetchInterviewers = async () => {
      try {
        setIsLoadingInterviewers(true);
        const response = await interviewerService.showInterviewers();
        
        // Handle different response structures and ensure we always have an array
        let interviewersData = [];
        
        if (response && Array.isArray(response)) {
          interviewersData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          interviewersData = response.data;
        } else if (response && response.interviewers && Array.isArray(response.interviewers)) {
          interviewersData = response.interviewers;
        } else if (response && typeof response === 'object') {
          // If response is an object but not an array, wrap it in an array
          interviewersData = [response];
        }
        
        setInterviewers(interviewersData);
        
      } catch (error) {
        console.error('Error fetching interviewers:', error);
        toast.error('Failed to load interviewers');
        // Ensure interviewers is always an array even on error
        setInterviewers([]);
      } finally {
        setIsLoadingInterviewers(false);
      }
    };

    fetchInterviewers();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      setNewInterviewer(prev => ({ ...prev, image: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setNewInterviewer(prev => ({ ...prev, image: null }));
    setImagePreview(null);
  };

  const handleAddInterviewer = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare FormData for multipart request
      const formData = new FormData();
      formData.append('name', newInterviewer.name);
      formData.append('email', newInterviewer.email);
      formData.append('password', newInterviewer.password);
      formData.append('role', newInterviewer.role);
      
      // Only append image if one is actually selected
      if (newInterviewer.image && newInterviewer.image instanceof File) {
        formData.append('image', newInterviewer.image);
        console.log('Image file added:', newInterviewer.image.name, newInterviewer.image.type);
      } else {
        console.log('No image file selected');
      }
      
      console.log('Sending FormData to API...');
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(key, `File: ${value.name} (${value.type})`);
        } else {
          console.log(key, value);
        }
      }
      
      // Call API to store interviewer
      const response = await interviewerService.storeInterviewer(formData);
      
      toast.success("Interviewer added successfully!");

      // Refresh the interviewer list from API
      try {
        const updatedResponse = await interviewerService.showInterviewers();
        
        // Handle different response structures and ensure we always have an array
        let updatedInterviewersData = [];
        
        if (updatedResponse && Array.isArray(updatedResponse)) {
          updatedInterviewersData = updatedResponse;
        } else if (updatedResponse && updatedResponse.data && Array.isArray(updatedResponse.data)) {
          updatedInterviewersData = updatedResponse.data;
        } else if (updatedResponse && updatedResponse.interviewers && Array.isArray(updatedResponse.interviewers)) {
          updatedInterviewersData = updatedResponse.interviewers;
        } else if (updatedResponse && typeof updatedResponse === 'object') {
          updatedInterviewersData = [updatedResponse];
        }
        
        setInterviewers(updatedInterviewersData);
      } catch (refreshError) {
        console.error('Error refreshing interviewers list:', refreshError);
        // Fallback to adding to local state if refresh fails
        const newId = (interviewers.length + 1).toString();
        const interviewer = {
          id: response.data?.id || newId,
          name: newInterviewer.name,
          email: newInterviewer.email,
          role: newInterviewer.role,
          image: response.data?.image || null,
          joinDate: new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
        };
        setInterviewers([...interviewers, interviewer]);
      }
      
      // Reset form
      setNewInterviewer({
        name: "",
        email: "",
        password: "",
        role: "Interviewer",
        image: null,
      });
      setImagePreview(null);
      
      setShowAddInterviewerModal(false);
      
    } catch (error) {
      console.error("Error adding interviewer:", error);
      console.error("Error response:", error.response?.data);
      
      if (error?.response?.status === 422) {
        const errorData = error.response?.data;
        
        // Check multiple possible error message locations
        const specificError = errorData?.error || errorData?.errors?.image?.[0] || errorData?.errors?.email?.[0] || '';
        const genericMessage = errorData?.message || 'Validation error occurred';
        
        // Show the specific error message if available, otherwise show generic message
        if (specificError) {
          // Handle specific common validation errors with user-friendly messages
          if (specificError.includes('image may not be greater than') || specificError.includes('2048 kilobytes')) {
            toast.error('Image file size is too large. Please choose an image smaller than 2MB.');
          } else if (specificError.includes('email has already been taken') || specificError.includes('already been taken')) {
            toast.error('This email address is already registered. Please use a different email.');
          } else if (specificError.includes('image must be a file of type')) {
            toast.error('Image must be JPEG, PNG, JPG, or GIF format.');
          } else {
            // Show the actual error message from backend
            toast.error(specificError);
          }
        } else {
          toast.error(genericMessage);
        }
      } else if (error?.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else {
        // For other errors, try to show specific error first, then message, then fallback
        const errorData = error?.response?.data;
        
        // Check multiple possible error message locations for non-422 errors
        const specificError = errorData?.error || errorData?.errors?.image?.[0] || errorData?.errors?.email?.[0] || '';
        const genericMessage = errorData?.message || 'Failed to add interviewer. Please try again.';
        
        // Apply the same user-friendly error handling for non-422 errors
        if (specificError) {
          if (specificError.includes('image may not be greater than') || specificError.includes('2048 kilobytes')) {
            toast.error('Image file size is too large. Please choose an image smaller than 2MB.');
          } else if (specificError.includes('email has already been taken') || specificError.includes('already been taken')) {
            toast.error('This email address is already registered. Please use a different email.');
          } else if (specificError.includes('image must be a file of type')) {
            toast.error('Image must be JPEG, PNG, JPG, or GIF format.');
          } else {
            toast.error(specificError);
          }
        } else {
          toast.error(genericMessage);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteInterviewer = (interviewer) => {
    setInterviewerToDelete(interviewer);
    setShowDeleteModal(true);
  };

  const confirmDeleteInterviewer = async () => {
    if (!interviewerToDelete) return;

    setIsDeleting(true);
    try {
      await interviewerService.deleteInterviewer(interviewerToDelete.id);
      
      // Remove from local state
      setInterviewers(interviewers.filter(int => int.id !== interviewerToDelete.id));
      
      toast.success("Interviewer deleted successfully!");
      setShowDeleteModal(false);
      setInterviewerToDelete(null);
    } catch (error) {
      console.error("Error deleting interviewer:", error);
      if (error?.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else {
        toast.error(error?.response?.data?.message || 'Failed to delete interviewer. Please try again.');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setInterviewerToDelete(null);
  };

  const closeModal = () => {
    // Reset form
    setNewInterviewer({
      name: "",
      email: "",
      password: "",
      role: "Interviewer",
      image: null,
    });
    setImagePreview(null);
    setShowAddInterviewerModal(false);
  };

  const updateInterviewerField = (field, value) => {
    setNewInterviewer((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <div className="bg-secondary border border-secondary-border rounded-lg mb-12 z-50">
        <div className="px-4 sm:px-8 py-6 border-b border-secondary-border flex items-center justify-between">
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
            Interviewer Management
          </h2>
          <button
            onClick={() => setShowAddInterviewerModal(true)}
            className="bg-primary text-white px-3 sm:px-4 py-2 rounded-lg flex items-center hover:bg-primary/90 transition-colors text-sm sm:text-base"
          >
            <Plus className="mr-2" size={16} />
            <span className="hidden sm:inline">Add Interviewer</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
        
        {/* Mobile Card Layout (hidden on lg and up) */}
        <div className="lg:hidden p-4 space-y-4">
          {isLoadingInterviewers ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin mr-2" size={24} />
              <span className="text-muted-foreground">Loading interviewers...</span>
            </div>
          ) : !Array.isArray(interviewers) || interviewers.length === 0 ? (
            <div className="bg-secondary border border-secondary-border rounded-lg p-8 text-center">
              <User className="mx-auto mb-2 text-muted-foreground" size={32} />
              <p className="text-muted-foreground">No interviewers found. Add your first interviewer to get started.</p>
            </div>
          ) : (
            interviewers.map((interviewer) => (
              <div key={interviewer.id} className="bg-background border border-secondary-border rounded-lg p-4">
                {/* Header with Profile and Actions */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-secondary border border-secondary-border flex items-center justify-center mr-3">
                      {interviewer.image ? (
                        <img 
                          src={interviewer.image} 
                          alt={interviewer.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img 
                          src={userIcon} 
                          alt="Default avatar"
                          className="w-6 h-6 object-contain"
                        />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-foreground">
                        {interviewer.name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {interviewer.email}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteInterviewer(interviewer)}
                    className="text-primary hover:text-primary/80 p-1"
                    title="Delete interviewer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-muted-foreground mb-1 text-xs">Role</div>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {interviewer.role || interviewer.role_name || 'Interviewer'}
                    </span>
                  </div>
                  
                  <div>
                    <div className="text-muted-foreground mb-1 text-xs">Join Date</div>
                    <div className="text-foreground font-medium text-xs">
                      {interviewer.joinDate || 
                       interviewer.join_date || 
                       interviewer.created_at ? 
                         new Date(interviewer.created_at).toLocaleDateString("en-US", {
                           month: "short",
                           day: "numeric",
                           year: "numeric",
                         }) : 
                         'N/A'
                      }
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table Layout (hidden on mobile/tablet) */}
        <div className="hidden lg:block p-8">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-border">
                  <th className="text-left py-3 px-4 font-semibold text-foreground">
                    Profile
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">
                    Name
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">
                    Email
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">
                    Role
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">
                    Join Date
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoadingInterviewers ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-muted-foreground">
                      <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                      Loading interviewers...
                    </td>
                  </tr>
                ) : !Array.isArray(interviewers) || interviewers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-muted-foreground">
                      No interviewers found. Add your first interviewer to get started.
                    </td>
                  </tr>
                ) : (
                  interviewers.map((interviewer) => (
                    <tr
                      key={interviewer.id}
                      className="border-b border-secondary-border/50 hover:bg-secondary/50"
                    >
                      <td className="py-4 px-4">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-secondary border border-secondary-border flex items-center justify-center">
                          {interviewer.image ? (
                            <img 
                              src={interviewer.image} 
                              alt={interviewer.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <img 
                              src={userIcon} 
                              alt="Default avatar"
                              className="w-6 h-6 object-contain"
                            />
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-foreground">
                        {interviewer.name}
                      </td>
                      <td className="py-4 px-4 text-muted-foreground">
                        {interviewer.email}
                      </td>
                      <td className="py-4 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {interviewer.role || interviewer.role_name || 'Interviewer'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-muted-foreground">
                        {interviewer.joinDate || 
                         interviewer.join_date || 
                         interviewer.created_at ? 
                           new Date(interviewer.created_at).toLocaleDateString("en-US", {
                             year: "numeric",
                             month: "long",
                             day: "numeric",
                           }) : 
                           'N/A'
                        }
                      </td>
                      <td className="py-4 px-4">
                        <button
                          onClick={() => handleDeleteInterviewer(interviewer)}
                          className="text-primary hover:text-primary/80 p-1"
                          title="Delete interviewer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Interviewer Modal */}
      <Modal
        isOpen={showAddInterviewerModal}
        onClose={closeModal}
        title="Add New Interviewer"
      >
        <div className="max-h-[70vh] overflow-y-auto">
          <form onSubmit={handleAddInterviewer}>
            <div className="space-y-6 pr-2">
              {/* Image Upload Section */}
              <div>
                <label className="block text-base font-semibold text-foreground mb-2">
                  Profile Image
                </label>
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-secondary border border-secondary-border flex items-center justify-center">
                    {imagePreview ? (
                      <img 
                        src={imagePreview} 
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User size={24} className="text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="image-upload"
                      disabled={isSubmitting}
                    />
                    <label
                      htmlFor="image-upload"
                      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg cursor-pointer hover:bg-primary/90 transition-colors"
                    >
                      <Upload size={16} className="mr-2" />
                      Choose Image
                    </label>
                    {newInterviewer.image && (
                      <button
                        type="button"
                        onClick={removeImage}
                        className="ml-2 text-primary hover:text-primary/80 text-sm"
                        disabled={isSubmitting}
                      >
                        Remove
                      </button>
                    )}
                    <p className="text-sm text-muted-foreground mt-1">
                      Max file size: 2MB. Supported formats: JPG, PNG, GIF
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-foreground mb-2">
                  Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]"
                  placeholder="Enter full name"
                  value={newInterviewer.name}
                  onChange={(e) => updateInterviewerField("name", e.target.value)}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-foreground mb-2">
                  Email <span className="text-destructive">*</span>
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]"
                  placeholder="interviewer@postscrypt.com"
                  value={newInterviewer.email}
                  onChange={(e) =>
                    updateInterviewerField("email", e.target.value)
                  }
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-foreground mb-2">
                  Password <span className="text-destructive">*</span>
                </label>
                <input
                  type="password"
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]"
                  placeholder="••••••••"
                  value={newInterviewer.password}
                  onChange={(e) =>
                    updateInterviewerField("password", e.target.value)
                  }
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-foreground mb-2">
                  Role <span className="text-destructive">*</span>
                </label>
                <select
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]"
                  value={newInterviewer.role}
                  onChange={(e) => updateInterviewerField("role", e.target.value)}
                  disabled={rolesLoading || isSubmitting}
                >
                  {rolesLoading ? (
                    <option value="">Loading roles...</option>
                  ) : availableRoles.length > 0 ? (
                    availableRoles.map((role) => (
                      <option key={role.id} value={role.title}>
                        {role.title}
                      </option>
                    ))
                  ) : (
                    <option value="Interviewer">Interviewer</option>
                  )}
                </select>
                {rolesLoading && (
                  <p className="mt-1 text-sm text-muted-foreground">Loading roles from backend...</p>
                )}
              </div>

              {/* Buttons moved inside the scrollable area */}
              <div className="flex space-x-3 mt-8 pt-4 border-t border-secondary-border">
                <button 
                  type="submit" 
                  className="flex-1 min-w-[180px] bg-primary text-white px-4 py-2 rounded-[4px] hover:bg-primary/90 transition-colors flex items-center justify-center whitespace-nowrap" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Adding Interviewer...
                    </>
                  ) : (
                    'Add Interviewer'
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-secondary border border-secondary-border text-foreground px-4 py-2 rounded-[4px] hover:bg-secondary/90 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteInterviewer}
        interviewerName={interviewerToDelete?.name}
        interviewerEmail={interviewerToDelete?.email}
        isDeleting={isDeleting}
      />
    </>
  );
} 